import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Loader2 } from "lucide-react";
import type { PersonaFormData, LibraryPersona } from "@/lib/persona-types";

interface PersonaEditorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  persona?: LibraryPersona | null;
  onSave: (data: PersonaFormData) => Promise<void>;
  isLoading?: boolean;
}

const defaultFormData: PersonaFormData = {
  name: "",
  role: "",
  goals: [],
  constraints: [],
  context: "",
  tags: [],
};

export function PersonaEditorModal({
  open,
  onOpenChange,
  persona,
  onSave,
  isLoading,
}: PersonaEditorModalProps) {
  const [form, setForm] = useState<PersonaFormData>(defaultFormData);
  const [tempGoal, setTempGoal] = useState("");
  const [tempConstraint, setTempConstraint] = useState("");
  const [tempTag, setTempTag] = useState("");

  useEffect(() => {
    if (persona) {
      setForm({
        name: persona.name,
        role: persona.role,
        goals: [...persona.goals],
        constraints: [...persona.constraints],
        context: persona.context,
        tags: [...persona.tags],
      });
    } else {
      setForm(defaultFormData);
    }
    setTempGoal("");
    setTempConstraint("");
    setTempTag("");
  }, [persona, open]);

  const handleAddGoal = () => {
    if (tempGoal.trim() && !form.goals.includes(tempGoal.trim())) {
      setForm({ ...form, goals: [...form.goals, tempGoal.trim()] });
      setTempGoal("");
    }
  };

  const handleAddConstraint = () => {
    if (tempConstraint.trim() && !form.constraints.includes(tempConstraint.trim())) {
      setForm({ ...form, constraints: [...form.constraints, tempConstraint.trim()] });
      setTempConstraint("");
    }
  };

  const handleAddTag = () => {
    if (tempTag.trim() && !form.tags.includes(tempTag.trim())) {
      setForm({ ...form, tags: [...form.tags, tempTag.trim()] });
      setTempTag("");
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.role.trim()) return;
    await onSave(form);
  };

  const isValid = form.name.trim() && form.role.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{persona ? "Edit Persona" : "Create Persona"}</DialogTitle>
          <DialogDescription>
            Define a user persona for context in your requirement reviews.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name and Role */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Sarah"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role/Title *</Label>
              <Input
                id="role"
                placeholder="e.g., Product Manager"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              />
            </div>
          </div>

          {/* Goals */}
          <div className="space-y-2">
            <Label>Goals</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Add a goal..."
                value={tempGoal}
                onChange={(e) => setTempGoal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddGoal();
                  }
                }}
              />
              <Button type="button" variant="outline" size="icon" onClick={handleAddGoal}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.goals.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.goals.map((goal, i) => (
                  <Badge key={i} variant="secondary" className="pl-3 pr-1 py-1">
                    <span className="mr-1">{goal}</span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, goals: form.goals.filter((_, j) => j !== i) })}
                      className="hover:text-destructive ml-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Constraints */}
          <div className="space-y-2">
            <Label>Constraints</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Add a constraint..."
                value={tempConstraint}
                onChange={(e) => setTempConstraint(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddConstraint();
                  }
                }}
              />
              <Button type="button" variant="outline" size="icon" onClick={handleAddConstraint}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.constraints.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.constraints.map((constraint, i) => (
                  <Badge key={i} variant="secondary" className="pl-3 pr-1 py-1">
                    <span className="mr-1">{constraint}</span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, constraints: form.constraints.filter((_, j) => j !== i) })}
                      className="hover:text-destructive ml-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Context */}
          <div className="space-y-2">
            <Label htmlFor="context">Context</Label>
            <Textarea
              id="context"
              placeholder="Additional context about this persona..."
              value={form.context}
              onChange={(e) => setForm({ ...form, context: e.target.value })}
              rows={3}
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags (optional)</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Add a tag..."
                value={tempTag}
                onChange={(e) => setTempTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button type="button" variant="outline" size="icon" onClick={handleAddTag}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.tags.map((tag, i) => (
                  <Badge key={i} variant="outline" className="pl-3 pr-1 py-1">
                    <span className="mr-1">{tag}</span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tags: form.tags.filter((_, j) => j !== i) })}
                      className="hover:text-destructive ml-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isLoading}>
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {persona ? "Save Changes" : "Create Persona"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
